import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarDays, RefreshCw, Phone, Wallet, TrendingUp, Clock3 } from 'lucide-react';
import api from '../../services/apiClient.ts';

const API_BASE = '/v1';
const toNumber = (v) => Number(String(v ?? '0').replace(/,/g, ''));
const fmtMoney = (v) => toNumber(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (d) => (d ? d : '—');

export default function OngoingInstallments() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await api.get(`${API_BASE}/installments/schedule`, {
        params: {
          paytype: 'installments',
          include_paid: 0,
          per_page: 500,
        },
      });
      setRows(resp?.data?.data || []);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || t('common.errorLoadingData');
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    let collected = 0;
    let remaining = 0;
    let overdueCount = 0;
    rows.forEach((r) => {
      collected += toNumber(r.collected ?? (toNumber(r.paid_amount) + toNumber(r.upfront_cash)));
      remaining += toNumber(r.remaining);
      if ((r.overdue_days ?? 0) > 0) overdueCount += 1;
    });
    return { total, collected, remaining, overdueCount };
  }, [rows]);

  const header = {
    title: t('navigation.InstallmentsOngoing') || 'الأقساط الجارية',
    subtitle: 'عرض كل الأقساط الجارية مع المتبقي وأيام التأخير',
  };

  return (
    <div className="p-6 space-y-6">
      <div className="rounded-2xl bg-gradient-to-r from-primary-600 via-primary-500 to-emerald-500 text-white shadow-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm opacity-80">{header.subtitle}</p>
            <h1 className="text-2xl font-bold mt-1">{header.title}</h1>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 rounded-xl border border-white/30 transition"
          >
            <RefreshCw className="w-4 h-4" />
            <span>{t('common.refresh')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={UsersBadge}
          title="عدد المسافرين بالأقساط"
          value={stats.total}
          tone="from-primary-50 to-primary-100 text-primary-700"
        />
        <StatCard
          icon={Wallet}
          title="المحصّل (نقد + أقساط)"
          value={`${fmtMoney(stats.collected)} د.ع`}
          tone="from-emerald-50 to-emerald-100 text-emerald-700"
        />
        <StatCard
          icon={TrendingUp}
          title="المتبقي"
          value={`${fmtMoney(stats.remaining)} د.ع`}
          tone="from-amber-50 to-amber-100 text-amber-700"
        />
        <StatCard
          icon={AlertTriangle}
          title="عدد المتأخرين"
          value={stats.overdueCount}
          tone="from-rose-50 to-rose-100 text-rose-700"
        />
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-50 text-rose-700 border border-rose-100">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-soft border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">جدول الأقساط الجارية</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">قيمة المحصل تشمل الدفعة النقدية المقدّمة.</p>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Clock3 className="w-4 h-4 animate-spin" />
              {t('common.loading')}
            </div>
          )}
        </div>

        <div className="app-table-wrap">
          <table className="app-table min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300">
              <tr>
                <Th>المجموعة</Th>
                <Th>المسافر</Th>
                <Th>الهاتف</Th>
                <Th>الاستحقاق القادم</Th>
                <Th>المحصّل</Th>
                <Th>المتبقي</Th>
                <Th>أيام التأخير</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800 text-gray-900 dark:text-gray-100">
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500 dark:text-gray-400">
                    لا توجد بيانات حالياً
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const collected = toNumber(row.collected ?? (toNumber(row.paid_amount) + toNumber(row.upfront_cash)));
                const groupName =
                  (row.group_name && String(row.group_name).trim()) ||
                  (row.groupName && String(row.groupName).trim()) ||
                  (row.gr_notes && String(row.gr_notes).trim()) ||
                  '';
                const groupLabel = groupName || `#${row.group_id}`;
                console.log('row', row);
                const phoneDisplay =
                  (row.traveler_phone && String(row.traveler_phone).trim()) ||
                  (row.phone1 && String(row.phone1).trim()) ||
                  (row.phone && String(row.phone).trim()) ||
                  'غير متوفر';
                return (
                  <tr key={`${row.group_id}-${row.traveler_id}`}>
                    <Td>
                      <div className="font-semibold">{groupLabel}</div>
                      <div className="text-xs text-gray-500">#{row.group_id}</div>
                    </Td>
                    <Td>
                      <div className="font-semibold">{row.traveler_name}</div>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{phoneDisplay}</span>
                      </div>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-gray-400" />
                        <span>{fmtDate(row.next_due_date)}</span>
                      </div>
                    </Td>
                    <Td className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {fmtMoney(collected)}
                    </Td>
                    <Td className="font-semibold text-amber-600 dark:text-amber-400">
                      {fmtMoney(row.remaining)}
                    </Td>
                    <Td>
                      {Number(row.overdue_days || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
                          <AlertTriangle className="w-4 h-4" />
                          {row.overdue_days}
                        </span>
                      ) : (
                        <span className="text-gray-500">0</span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, title, value, tone }) => (
  <div className={`rounded-2xl border border-gray-100 dark:border-gray-800 bg-gradient-to-br ${tone} p-4 shadow-sm`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-600/80 dark:text-gray-300/80">{title}</p>
        <p className="text-xl font-bold mt-1 text-gray-900 dark:text-gray-50">{value}</p>
      </div>
      <div className="p-2 rounded-xl bg-white/70 dark:bg-gray-900/50 border border-gray-200/60 dark:border-gray-700/60 text-gray-700 dark:text-gray-200">
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const UsersBadge = (props) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M23 21v-2a4 4 0 00-3-3.87"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 3.13a4 4 0 010 7.75"/></svg>;

const Th = ({ children }) => (
  <th scope="col" className="px-4 py-3 text-right font-semibold align-middle whitespace-nowrap">
    {children}
  </th>
);

const Td = ({ children }) => (
  <td className="px-4 py-3 align-middle">
    {children}
  </td>
);
